import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import InfiniteScroll from 'components/InfiniteScroll';
import { MessageForm } from 'components/MessageForm';
import { NoteCard } from 'components/NoteCard';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import DealContext from '../../providers/Deal';
import { BOSS_MODE_TAG, extractTagsFromText } from './utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import classNames from 'classnames';
import { Note, NoteTag } from 'models/Note';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import useChatScroll from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/pages/Chat/hooks/useChatScroll';
import useCallbackRef from 'hooks/useCallbackRef';
import { focusElement } from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/pages/Chat/helpers';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import useUploadMessageFile from 'hooks/useUploadMessageFile';
import { MessageFileHandlers } from 'components/MessageFiles/components/MessageFile/MessageFile.component';
import MessagesEmptyState from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/pages/Chat/components/MessagesEmptyState';
import NotesEmptyImage from 'assets/images/notes-empty-state.webp';
import { trimRichTextValueFromJson } from 'components/RichTextEditorField/utils';
import { ReactionType, ReactionUsers } from 'models/Reactions';

import './NotesList.styles.scss';

type NotesListProps = {
  className?: string;
  loading?: boolean;
  notes?: Note[];
  notesAbout: string;
  emptyMessage?: string;
  handleOnSendNote: (
    value: string,
    tags: NoteTag[],
    uploadedFiles: DropzoneFile[],
  ) => Promise<void>;
  removeNote: (noteId: string) => Promise<void>;
  onNotesScroll: () => void;
  onUploadReq: (
    file: File,
    config: AxiosRequestConfig,
  ) => Promise<DropzoneFile>;
  onReact: (noteId: string, reaction: ReactionType) => void;
  getReactionUsers: (noteId: string) => Promise<AxiosResponse<ReactionUsers>>;
} & MessageFileHandlers;

const NotesList: FC<NotesListProps> = ({
  className,
  notes,
  loading,
  notesAbout,
  emptyMessage,
  handleOnSendNote,
  onNotesScroll,
  onUploadReq,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasNewNotes, setHasNewNotes] = useState(false);

  const { colleagues } = useContext(DealContext);
  const { currentUser } = useContext(CurrentUserContext);

  const [notesContainer, notesContainerRef] =
    useCallbackRef<InfiniteScrollRef>();

  const { t } = useTranslation();

  const { shouldScrollTL, scrollToBottom } = useChatScroll({
    condition: hasNewNotes,
    container: notesContainer?.container,
  });

  const {
    files,
    uploadedFiles,
    areAllFilesProcessed,
    restRootProps,
    clearFiles,
    onFileRemove,
    onFileUpload,
    getInputProps,
  } = useUploadMessageFile({
    onUploadReq,
  });

  const tagOptions = useMemo(
    () => [
      ...colleagues
        .filter(
          ({ userId, isAccepted }) => userId !== currentUser?._id && isAccepted,
        )
        .map(({ userId, name, email }) => ({
          id: userId,
          display: name || email,
        })),
      { id: BOSS_MODE_TAG, isRequired: true, display: t('resolveSupport') },
    ],
    [colleagues, currentUser?._id, t],
  );

  const loadingSpinner = useMemo(
    () => (loading || isLoading ? <Loader overlay={false} /> : null),
    [isLoading, loading],
  );

  const scrollToView = useCallback(() => {
    setHasNewNotes(false);
    scrollToBottom();
  }, [scrollToBottom]);

  const handleSubmit = useCallback(
    async (note: string) => {
      setIsLoading(true);

      const trimmedNote = trimRichTextValueFromJson(note);

      const extractedTags = extractTagsFromText(trimmedNote);

      await handleOnSendNote(trimmedNote, extractedTags, uploadedFiles);

      setHasNewNotes(true);
      setIsLoading(false);
      clearFiles();
      if (shouldScrollTL) scrollToView();
      focusElement('#message-form-input');
    },
    [shouldScrollTL, uploadedFiles, handleOnSendNote, scrollToView, clearFiles],
  );

  return (
    <div className={classNames('notes-list', className)} {...restRootProps}>
      <input {...getInputProps()} />
      <div className="notes-list__wrapper">
        <InfiniteScroll
          ref={notesContainerRef}
          className="notes-list__wrapper__notes"
          localScroll
          onScroll={onNotesScroll}
        >
          {notes?.length
            ? notes.map((note) => (
                <NoteCard
                  key={note._id}
                  note={note}
                  setLoading={setIsLoading}
                  {...rest}
                />
              ))
            : !loadingSpinner && (
                <MessagesEmptyState
                  text={t('emptyStateNotes', { name: notesAbout })}
                  description={emptyMessage}
                  imageSrc={NotesEmptyImage}
                />
              )}

          {loadingSpinner}
        </InfiniteScroll>

        <MessageForm
          canTagPeople
          buttonText={t('saveNote')}
          placeholder={t('tagResolveColleaguesHint')}
          tagOptions={tagOptions}
          resetMessage={loading}
          files={files}
          areAllFilesProcessed={areAllFilesProcessed}
          uploadFileHintMessage={t('hintYouCanDragFilesToNotes')}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          handleOnSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default NotesList;
